import React from 'react';
import { FinalCountdown } from '@/store/videoFeatures/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { interpolateCountdownText } from '@/utils/countdownTextInterpolation';
import { tw4 } from '@/utils/i18n/wave4';

interface FinalCountdownOverlayProps {
  config: FinalCountdown;
  timeRemaining: number;
  onRedirect: () => void;
  /** Só deve vir preenchido quando `showButton && canGo` (há para onde ir). */
  onSkip?: () => void;
  /** true apenas quando a navegação REALMENTE aconteceu. */
  hasRedirected?: boolean;
  /** Dispensa o overlay. Sem isto o card não tem botão de fechar. */
  onDismiss?: () => void;
}

export const FinalCountdownOverlay: React.FC<FinalCountdownOverlayProps> = ({
  config,
  timeRemaining,
  onRedirect,
  onSkip,
  hasRedirected = false,
  onDismiss
}) => {
  const isFinished = timeRemaining <= 0;
  // "Redirecionando..." só quando alguém de fato foi redirecionado. Zerar a
  // contagem sem navegar (modo aviso, URL inválida, pop-up bloqueado) não é
  // redirecionar — e nesse estado o botão precisa continuar na tela.
  const isRedirecting = isFinished && hasRedirected;
  // Zerou e ninguém saiu: o card tem que oferecer como sair.
  const needsDismiss = isFinished && !hasRedirected && !!onDismiss;
  const description = isRedirecting 
    ? 'Redirecionando...' 
    : interpolateCountdownText(config.description, timeRemaining);
  
  const cardSizeClass = config.fullScreen 
    ? 'w-full h-full rounded-none' 
    : {
        small: 'w-auto max-w-[280px]',
        medium: 'w-auto max-w-[340px]',
        large: 'w-auto max-w-[420px]'
      }[config.cardSize] || 'w-auto max-w-[340px]';
  
  const animationClass = {
    fade: 'animate-fade-in',
    fadeZoom: 'animate-scale-in',
    slideUp: 'animate-fade-in'
  }[config.animation];
  
  const circleSizeClass = {
    small: 'w-16 h-16 text-2xl',
    medium: 'w-20 h-20 text-3xl',
    large: 'w-24 h-24 text-4xl'
  }[config.circleSize];
  
  return (
    <>
      {/* Backdrop */}
      <div
        className="absolute inset-0 z-[50]"
        style={{
          backgroundColor: config.overlayColor || '#000000',
          opacity: (config.overlayOpacity ?? 70) / 100
        }}
      />
      
      {/* Card container */}
      <div
        className={cn(
          "absolute inset-0 z-[51] flex items-center justify-center transition-opacity duration-300",
          config.forcedMode && "cursor-not-allowed"
        )}
      >
        <div
          className={cn(
            "text-center space-y-4",
            config.fullScreen 
              ? "w-full h-full flex flex-col items-center justify-center p-12 rounded-none" 
              : `p-6 sm:p-8 ${cardSizeClass}`,
            animationClass
          )}
          style={{
            backgroundColor: config.cardColor,
            opacity: config.cardOpacity / 100,
            borderRadius: config.fullScreen ? '0' : `${config.cardBorderRadius}px`
          }}
        >
        {/* Título */}
        <h2 
          className="text-2xl font-bold px-2 py-0.5 rounded inline-block"
          style={{ color: config.titleColor }}
        >
          {config.title}
        </h2>
        
        {/* Cronômetro visual */}
        <div className="flex items-center justify-center">
          <div 
            className={cn(
              "rounded-full flex items-center justify-center font-bold transition-all",
              circleSizeClass
            )}
            style={{
              backgroundColor: config.circleColor,
              color: config.circleTextColor
            }}
          >
            {timeRemaining}
          </div>
        </div>
        
        {/* Descrição */}
        <p 
          className="text-base px-2 py-0.5 rounded inline-block"
          style={{ color: config.descriptionColor }}
        >
          {description}
        </p>
        
        {/* Botão "Ir Agora". Fica na tela até a navegação acontecer de fato —
            se o pop-up foi bloqueado, o clique (gesto do usuário) ainda passa. */}
        {config.showButton && onSkip && !isRedirecting && (
          <Button
            onClick={onSkip}
            className="mx-auto px-6 py-2 mt-4 transition-transform hover:scale-105"
            style={{
              backgroundColor: config.buttonColor,
              color: config.buttonTextColor,
              borderRadius: `${config.buttonBorderRadius}px`
            }}
          >
            {config.buttonText}
          </Button>
        )}

        {/* Saída do overlay: a contagem zerou e ninguém foi redirecionado.
            Sem isto o card cobre o vídeo para sempre. */}
        {needsDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="mx-auto mt-3 block text-[12px] underline underline-offset-2 opacity-70 hover:opacity-100 transition-opacity"
            style={{ color: config.descriptionColor }}
          >
            {tw4('fc.dismiss')}
          </button>
        )}
      </div>
    </div>
  </>
  );
};
